import { useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useState, useEffect } from 'react';
import { pb } from '@/lib/pocketbase';
import { isAdmin, isTutor, isStudent, isAdvertising } from '@/utils/roleValidation';
import TutorProfile from '@/pages/TutorProfile';
import StudentProfile from '@/pages/StudentProfile';
import AdvertisingProfile from '@/pages/AdvertisingProfile';

const ProfileRouter = () => {
  const { id } = useParams();
  const { user: currentUser, isLoading: authLoading } = useAuth();
  const [profileUser, setProfileUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingStatusChecked, setPendingStatusChecked] = useState(false);

  // If no id in params, use current user
  const targetUserId = id || currentUser?.id;

  // Remove console logs to prevent spam

  useEffect(() => {
    const fetchUserProfile = async () => {
      const profileStart = performance.now();
      console.log('🔍 [PROFILE ROUTER] Starting profile fetch', { targetUserId });
      
      if (!targetUserId) {
        console.log('❌ [PROFILE ROUTER] No user ID provided');
        setError('No user ID provided');
        setIsLoading(false);
        setPendingStatusChecked(true);
        return;
      }

      try {
        const fetchStart = performance.now();
        console.log('📡 [PROFILE ROUTER] Fetching user from PocketBase');
        const userData = await pb.collection('users').getOne(targetUserId);
        const fetchDuration = performance.now() - fetchStart;
        console.log(`✅ [PROFILE ROUTER] User fetched in ${fetchDuration.toFixed(2)}ms`, {
          userId: userData.id,
          role: userData.role
        });
        
        // Pre-check pending status for tutors to avoid button flashing
        if (userData.role === 'tutor' && currentUser?.id === userData.id) {
          console.log('🔄 [PROFILE ROUTER] Pre-checking pending status for tutor');
          try {
            // Quick parallel checks
            await Promise.all([
              pb.collection('tutor_pending_updates').getList(1, 1, {
                filter: `(tutor_id = "${userData.id}" || tutor_email = "${userData.email}") && status = "pending"`,
              }),
              pb.collection('profile_update_notifications').getList(1, 1, {
                filter: `tutor_id = "${userData.id}" && type = "pending"`,
              })
            ]);
            console.log('✅ [PROFILE ROUTER] Pending status pre-checked');
          } catch (error) {
            console.log('⚠️ [PROFILE ROUTER] Pending status check failed (non-critical)');
          }
        }
        
        setProfileUser(userData);
        setError(null);
        setPendingStatusChecked(true);
        
        const totalDuration = performance.now() - profileStart;
        console.log(`🎉 [PROFILE ROUTER] Complete in ${totalDuration.toFixed(2)}ms`);
      } catch (error) {
        const errorDuration = performance.now() - profileStart;
        console.error(`❌ [PROFILE ROUTER] Error after ${errorDuration.toFixed(2)}ms:`, error);
        setError('User not found');
        setPendingStatusChecked(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (!authLoading) {
      console.log('🚀 [PROFILE ROUTER] Auth loaded, starting fetch');
      fetchUserProfile();
    } else {
      console.log('⏳ [PROFILE ROUTER] Waiting for auth to load');
    }
  }, [targetUserId, authLoading, currentUser?.id]);

  // Show loading while authentication or profile is being determined
  if (authLoading || isLoading || !pendingStatusChecked) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center gap-4 w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="text-gray-600 text-center">Loading profile...</span>
        </div>
      </div>
    );
  }

  // Handle errors
  if (error || !profileUser) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Profile Not Found</h1>
          <p className="text-gray-600">{error || 'The user profile you\'re looking for doesn\'t exist.'}</p>
        </div>
      </div>
    );
  }

  // Route to appropriate profile component based on user role
  // Check role directly from the profileUser object for more reliable routing
  const userRole = profileUser?.role;
  
  if (userRole === 'tutor') {
    return <TutorProfile />;
  } else if (userRole === 'student') {
    // Pass the profileUser data to StudentProfile
    return <StudentProfile profileUser={profileUser} />;
  } else if (userRole === 'admin') {
    // Admins can view tutor profile interface for now
    return <TutorProfile />;
  } else if (userRole === 'advertising') {
    return <AdvertisingProfile profileUser={profileUser} />;
  }

  // Fallback for unknown roles - but first log what we're getting for debugging
  console.log('ProfileRouter - Unknown role fallback triggered:', {
    profileUser,
    userRole,
    targetUserId
  });

  // Fallback for unknown roles
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">Profile Setup Required</h1>
        <p className="text-gray-600 mb-4">
          User role: <strong>{userRole || 'undefined'}</strong>
        </p>
        <p className="text-gray-600">
          Please contact support to set up this profile properly.
        </p>
      </div>
    </div>
  );
};

export default ProfileRouter;
